// packages/frontend/src/services/tokenService.ts

import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from './firebase';
import { logger } from '../utils/logger';

// Storage keys
const TOKEN_KEY = 'firebase_auth_token';
const USER_KEY = 'firebase_user_data';
const REFRESH_TIME_KEY = 'firebase_token_refresh_time';

// Token refresh configuration
// const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes before expiry
const MAX_RETRY_ATTEMPTS = 3;

// Types
export interface StoredUserData {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  lastLogin: number;
}

export interface TokenRefreshResult {
  success: boolean;
  token?: string;
  error?: string;
}

// Storage utilities
class SecureStorage {
  private static isAvailable(): boolean {
    try {
      return typeof window !== 'undefined' && 'localStorage' in window;
    } catch {
      return false;
    }
  }

  static setItem(key: string, value: string): void {
    if (!this.isAvailable()) return;
    
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      logger.warn('Failed to store data in localStorage:', error);
    }
  }

  static getItem(key: string): string | null {
    if (!this.isAvailable()) return null;
    
    try {
      return localStorage.getItem(key);
    } catch (error) {
      logger.warn('Failed to retrieve data from localStorage:', error);
      return null;
    }
  }

  static removeItem(key: string): void {
    if (!this.isAvailable()) return;
    
    try {
      localStorage.removeItem(key);
    } catch (error) {
      logger.warn('Failed to remove data from localStorage:', error);
    }
  }

  static clear(): void {
    if (!this.isAvailable()) return;
    
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(REFRESH_TIME_KEY);
    } catch (error) {
      logger.warn('Failed to clear localStorage:', error);
    }
  }
}

// Token management class
class TokenManager {
  private refreshTimer: NodeJS.Timeout | null = null;
  private retryCount = 0;
  private isRefreshing = false;

  /**
   * Store user data and token in secure storage
   */
  storeUserData(user: User, token: string): void {
    const userData: StoredUserData = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      emailVerified: user.emailVerified,
      lastLogin: Date.now(),
    };

    SecureStorage.setItem(USER_KEY, JSON.stringify(userData));
    SecureStorage.setItem(TOKEN_KEY, token);
    SecureStorage.setItem(REFRESH_TIME_KEY, Date.now().toString());
  }

  /**
   * Retrieve stored user data
   */
  getStoredUserData(): StoredUserData | null {
    const userData = SecureStorage.getItem(USER_KEY);
    if (!userData) return null;

    try {
      return JSON.parse(userData) as StoredUserData;
    } catch (error) {
      logger.warn('Failed to parse stored user data:', error);
      return null;
    }
  }

  /**
   * Retrieve stored token
   */
  getStoredToken(): string | null {
    return SecureStorage.getItem(TOKEN_KEY);
  }

  /**
   * Clear all stored authentication data
   */
  clearStoredData(): void {
    SecureStorage.clear();
    this.clearRefreshTimer();
  }

  /**
   * Check if token needs refresh
   */
  needsRefresh(): boolean {
    const refreshTime = SecureStorage.getItem(REFRESH_TIME_KEY);
    if (!refreshTime) return true;

    const lastRefresh = parseInt(refreshTime, 10);
    const timeSinceRefresh = Date.now() - lastRefresh;
    
    // Refresh if more than 50 minutes have passed (tokens expire after 1 hour)
    return timeSinceRefresh > (60 - 10) * 60 * 1000;
  }

  /**
   * Refresh the authentication token
   */
  async refreshToken(): Promise<TokenRefreshResult> {
    if (this.isRefreshing) {
      return { success: false, error: 'Token refresh already in progress' };
    }

    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'No authenticated user' };
    }

    this.isRefreshing = true;
    this.retryCount = 0;

    try {
      const token = await user.getIdToken(true); // Force refresh
      
      // Update stored data
      this.storeUserData(user, token);
      
      // Reset retry count on success
      this.retryCount = 0;
      
      return { success: true, token };
    } catch (error) {
      logger.error('Token refresh failed:', error);
      
      // Retry logic
      if (this.retryCount < MAX_RETRY_ATTEMPTS) {
        this.retryCount++;
        logger.info(`Retrying token refresh (attempt ${this.retryCount}/${MAX_RETRY_ATTEMPTS})`);
        
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * this.retryCount));
        return this.refreshToken();
      }
      
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Token refresh failed' 
      };
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Set up automatic token refresh
   */
  setupTokenRefresh(): void {
    this.clearRefreshTimer();
    
    // Check every 5 minutes if token needs refresh
    this.refreshTimer = setInterval(async () => {
      if (this.needsRefresh() && auth.currentUser) {
        logger.info('Token needs refresh, attempting refresh...');
        const result = await this.refreshToken();
        
        if (!result.success) {
          logger.error('Automatic token refresh failed:', result.error);
          // Could emit an event here to notify the app
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
  }

  /**
   * Clear the refresh timer
   */
  clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Get a valid token (refresh if needed)
   */
  async getValidToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (!user) return null;

    try {
      // Check if we need to refresh
      if (this.needsRefresh()) {
        const refreshResult = await this.refreshToken();
        if (refreshResult.success && refreshResult.token) {
          return refreshResult.token;
        }
      }

      // Get current token
      return await user.getIdToken();
    } catch (error) {
      logger.error('Failed to get valid token:', error);
      return null;
    }
  }
}

// Create singleton instance
const tokenManager = new TokenManager();

// Authentication state listener
let authStateListener: (() => void) | null = null;

/**
 * Set up authentication state listener
 */
export const setupAuthStateListener = (onAuthChange: (user: User | null) => void): void => {
  // Clean up existing listener
  if (authStateListener) {
    authStateListener();
  }

  // Set up new listener
  authStateListener = onAuthStateChanged(auth, async (user) => {
    if (user) {
      // User signed in
      try {
        const token = await user.getIdToken();
        tokenManager.storeUserData(user, token);
        tokenManager.setupTokenRefresh();
      } catch (error) {
        logger.error('Failed to get token for authenticated user:', error);
      }
    } else {
      // User signed out
      tokenManager.clearStoredData();
    }

    onAuthChange(user);
  });
};

/**
 * Clean up authentication state listener
 */
export const cleanupAuthStateListener = (): void => {
  if (authStateListener) {
    authStateListener();
    authStateListener = null;
  }
  tokenManager.clearRefreshTimer();
};

/**
 * Get stored user data
 */
export const getStoredUserData = (): StoredUserData | null => {
  return tokenManager.getStoredUserData();
};

/**
 * Get stored token
 */
export const getStoredToken = (): string | null => {
  return tokenManager.getStoredToken();
};

/**
 * Get a valid token (refresh if needed)
 */
export const getValidToken = (): Promise<string | null> => {
  return tokenManager.getValidToken();
};

/**
 * Refresh the current token
 */
export const refreshToken = (): Promise<TokenRefreshResult> => {
  return tokenManager.refreshToken();
};

/**
 * Clear all stored authentication data
 */
export const clearStoredData = (): void => {
  tokenManager.clearStoredData();
};

/**
 * Check if token needs refresh
 */
export const needsTokenRefresh = (): boolean => {
  return tokenManager.needsRefresh();
};

// Export the token manager instance for advanced usage
export { tokenManager };
